import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { SchedulerItemApiAbstract } from '../../../common/scheduler/api/item-api';
import { ApiRouteSchedulingService } from '../../../api/elite/routes/scheduling.service';
import { ApiRouteSubWorkOrderService } from '../../../api/elite/routes/sub-work-order.service';
import { ApiRouteInspectionAssemblyItemService } from '../../../api/elite/routes/inspection-assembly-item.service';
import { InspectionAssemblyItem } from '../../../api/elite/types/inspection-assembly-item';
import { SchedulingApiRoutesDto } from '../../../api/elite/dto/scheduling.dto';
import { ApiSubWorkOrderDto } from '../../../api/elite/dto/sub-work-order.dto';
import { Department } from '../../../api/elite/types/department';
import { ApiRouteDepartmentService } from '../../../api/elite/routes/department.service';
import { User } from '../../../api/backend/types/users';
import { ApiRouteUserService } from '../../../api/elite/routes/users.service';
import { TrimmingInspectionPayload } from '../interfaces/trimming-inspection.interface';
import { ApiRouteInspectionService } from 'src/app/api/elite/routes/inspection.service';
import { ValidScrapOperator } from '../interfaces/valid-scrap-operator.interface';
import { ValidMachine } from '../interfaces/valid-machine.interface';
import { ApiRouteMachinesService } from '../../../api/elite/routes/machines.service';

@Injectable({
  providedIn: 'root',
})
export class SchedulerStageItemApiService extends SchedulerItemApiAbstract {
  protected readonly apiRouteScheduling = inject(ApiRouteSchedulingService);
  protected readonly apiSubWorkOrderRouter = inject(
    ApiRouteSubWorkOrderService
  );
  protected readonly apiRouteInspectionAssemblyItemService = inject(
    ApiRouteInspectionAssemblyItemService
  );
  protected readonly departmentApiRouter = inject(ApiRouteDepartmentService);
  protected readonly apiRouteUserService = inject(ApiRouteUserService);
  protected readonly apiRouteMachinesService = inject(ApiRouteMachinesService);
  protected readonly apiRouteInspection = inject(ApiRouteInspectionService);

  getItemMeasure(itemName: string): Observable<InspectionAssemblyItem> {
    return this.apiRouteInspectionAssemblyItemService.getMeasureByName(
      itemName
    );
  }

  finishSubWorkOrderStage(
    body: SchedulingApiRoutesDto['registerStageProgress']['body']
  ): Observable<any> {
    return this.apiRouteScheduling.registerProgressStage(body);
  }

  quantityOnStages(
    workOrderId: number
  ): Observable<ApiSubWorkOrderDto['quantityOnStages']['res']> {
    return this.apiSubWorkOrderRouter.quantityOnStages(workOrderId);
  }

  updateRank(itemId: number, rank: string): Observable<any> {
    return undefined;
  }

  getDepartmentData(department: string): Observable<Department> {
    return this.departmentApiRouter.getDataV2(department, {
      includeReasonScrap: true,
      includeShifts: true,
    });
  }

  getOperators(department: string, role = 'Auxiliar'): Observable<User[]> {
    return this.apiRouteUserService.allUsersV2(department, role);
  }

  getData(
    department: string,
    role: string
  ): Observable<{ department: Department; operators: User[] }> {
    return forkJoin({
      department: this.getDepartmentData(department),
      operators: role ? this.getOperators(department, role) : of([]),
    });
  }

  saveTrimmingInspection(payload: TrimmingInspectionPayload) {
    return this.apiRouteInspection.saveTrimmingInspection(payload);
  }

  getValidOperators(transactionNumber: string): Observable<ValidScrapOperator[]> {
    return this.apiRouteUserService.getValidOperators(transactionNumber);
  }

  getValidMachines(transactionNumber: string, userId?: number): Observable<ValidMachine[]> {
    return this.apiRouteMachinesService.getValidMachines(transactionNumber, userId);
  }

  getTrimmingSpecs(workOrder: string | number): Observable<any[]> {
    return this.apiRouteInspection.getTrimmingSpecs(workOrder);
  }
}
